import {Component, inject, Input, signal, ChangeDetectionStrategy} from '@angular/core';
import {ChartData, ChartOptions} from 'chart.js';

import {EnergyBalanceResponse} from '../../../../api/models/energy-balance-response';
import {LocationStateService} from '../../../../common/services/location-state.service';

export interface BalanceDataRow {
  isFirstElement?: boolean;
  label: string;
  value: string;
  isTotal?: boolean;
  isDate?: boolean;
  isDifferent?: boolean;
}

@Component({
  selector: 'app-base-chart',
  standalone: true,
  imports: [],
  changeDetection: ChangeDetectionStrategy.Eager,
  template: '',
})
export class BaseChartComponent {
  chartData = signal<ChartData<'bar'>>({labels: [], datasets: []});
  chartOptions = signal<ChartOptions<'bar'>>({});
  chart: any;
  @Input() loading: boolean = false;
  isAccordionOpen = true;   //Details
  isIndependenceAccordionOpen =  true;  //Unabhaengigkeit
  balanceData: BalanceDataRow[][] = [];
  independenceData: BalanceDataRow[][] = [];

  protected readonly locationStateService = inject(LocationStateService);

  /**
   * The timezone of the currently selected location. When the location has no
   * timezone set, this is `undefined` and date formatting uses the runtime default.
   */
  protected get timezone(): string | undefined {
    return this.locationStateService.currentLocation()?.timezone;
  }

  toggleDetailsAccordion() {
    this.isAccordionOpen = !this.isAccordionOpen;
  }

  toggleIndependenceAccordion() {
    this.isIndependenceAccordionOpen = !this.isIndependenceAccordionOpen;
  }

  protected getEnergyFromBattery(data: EnergyBalanceResponse, useCorrectedValues = false): number {
    if (useCorrectedValues) {
      return data?.energyFromBattery_corrected ?? data?.energyFromBattery ?? 0;
    }
    return data?.energyFromBattery ?? 0;
  }

  protected getEnergyToGrid(data: EnergyBalanceResponse, useCorrectedValues = false): number {
    if (useCorrectedValues) {
      return data?.energyToGrid_corrected ?? data?.energyToGrid ?? 0;
    }
    return data?.energyToGrid ?? 0;
  }

  protected getSelfConsumptionFromConsumption(data: EnergyBalanceResponse, useCorrectedValues = false): number {
    if (useCorrectedValues) {
      return data?.selfConsumptionFromConsumption_corrected ?? data?.selfConsumptionFromConsumption ?? 0;
    }
    return data?.selfConsumptionFromConsumption ?? 0;
  }

  protected getSelfConsumptionFromProduction(data: EnergyBalanceResponse, useCorrectedValues = false): number {
    if (useCorrectedValues) {
      return data?.selfConsumptionFromProduction_corrected ?? data?.selfConsumptionFromProduction ?? 0;
    }
    return data?.selfConsumptionFromProduction ?? 0;
  }

  getTotalConsumption(data: EnergyBalanceResponse, useCorrectedValues = false): number {
    //const consumed = data?.energyConsumed ?? 0;
    const selfConsumptionC = this.getSelfConsumptionFromConsumption(data, useCorrectedValues);
    const fromBattery = this.getEnergyFromBattery(data, useCorrectedValues);
    const fromGrid = data?.energyFromGrid ?? 0;
    return fromBattery + fromGrid + selfConsumptionC;
  }

  getTotalProduction(data: EnergyBalanceResponse, useCorrectedValues = false): number {
    //const production = data?.energyFromSolar ?? 0;
    const selfConsumptionP = this.getSelfConsumptionFromProduction(data, useCorrectedValues);
    const toBattery = data?.energyToBattery ?? 0;
    const toGrid = this.getEnergyToGrid(data, useCorrectedValues);
    //const consToProd = data?.energyFromConsumptionToProduction ?? 0;
    return selfConsumptionP + toBattery + toGrid;

  }

  protected getBalanceDataRow(data: EnergyBalanceResponse, useCorrectedValues = false): BalanceDataRow[] {
    const formattedDate = data.date
      ? new Date(data.date).toLocaleDateString('de-DE', {
          weekday: 'long',
          year: 'numeric',
          month: 'long',
          day: 'numeric',
        })
      : '';

    const selfConsumptionFromProduction = parseFloat(this.getSelfConsumptionFromProduction(data, useCorrectedValues).toFixed(2));
    const selfConsumptionFromConsumption = parseFloat(this.getSelfConsumptionFromConsumption(data, useCorrectedValues).toFixed(2));
    const isDifferent = Math.abs(selfConsumptionFromProduction - selfConsumptionFromConsumption) > 0.01;

    const totalProduction = this.getTotalProduction(data, useCorrectedValues);
    const totalConsumption = Math.abs(this.getTotalConsumption(data, useCorrectedValues));

    const formatValueWithPercentage = (value: number, total: number): string => {
      const percentage = total > 0 ? (value / total) * 100 : 0;
      return `${value.toFixed(2)} (${percentage.toFixed(1)}%)`;
    };

    return [
      {
        label: '',
        value: formattedDate,
        isDate: true,
      },
      {
        label: 'Gesamt-Produktion',
        value: `${totalProduction.toFixed(2)} (100.0%)`,
        isTotal: true,
        isFirstElement: true,
      },
      // {
      //   label: 'Produktion',
      //   value: formatValueWithPercentage(data?.energyFromSolar ?? 0, totalProduction)
      // },
      {
        label: 'Zur Batterie',
        value: formatValueWithPercentage(data?.energyToBattery ?? 0, totalProduction),
      },
      {
        label: 'Eigenverbrauch',
        value: formatValueWithPercentage(this.getSelfConsumptionFromProduction(data, useCorrectedValues), totalProduction),
        isDifferent: isDifferent,
      },
      {
        label: 'Verkauf',
        value: formatValueWithPercentage(this.getEnergyToGrid(data, useCorrectedValues), totalProduction),
      },
      // {
      //   label: 'Verbrauch zu Produktion',
      //   value: formatValueWithPercentage(data?.energyFromConsumptionToProduction ?? 0, totalProduction)
      // },
      {
        label: 'Gesamt-Verbrauch',
        value: `${totalConsumption.toFixed(2)} (100.0%)`,
        isTotal: true,
      },
      // {
      //   label: 'Aus Produktion',
      //   value: formatValueWithPercentage(data?.energyConsumed ?? 0, totalConsumption),
      // },
      {
        label: 'Aus Batterie',
        value: formatValueWithPercentage(this.getEnergyFromBattery(data, useCorrectedValues), totalConsumption),
      },
      {
        label: 'Eigenverbrauch',
        value: formatValueWithPercentage(this.getSelfConsumptionFromConsumption(data, useCorrectedValues), totalConsumption),
        isDifferent: isDifferent,
      },
      {
        label: 'Einkauf',
        value: formatValueWithPercentage(data?.energyFromGrid ?? 0, totalConsumption),
      },
    ];
  }

  protected getAggregatedBalanceDataRow(data: EnergyBalanceResponse[], useCorrectedValues = false): BalanceDataRow[] {
    const aggregatedEnergyFromBattery = data.reduce((sum, item) => sum + this.getEnergyFromBattery(item, useCorrectedValues), 0);
    const aggregatedEnergyToGrid = data.reduce((sum, item) => sum + this.getEnergyToGrid(item, useCorrectedValues), 0);
    const aggregatedSelfConsumptionFromProduction = data.reduce(
      (sum, item) => sum + this.getSelfConsumptionFromProduction(item, useCorrectedValues),
      0,
    );
    const aggregatedSelfConsumptionFromConsumption = data.reduce(
      (sum, item) => sum + this.getSelfConsumptionFromConsumption(item, useCorrectedValues),
      0,
    );

    const aggregatedData: EnergyBalanceResponse = {
      date: undefined,
      energyFromSolar: data.reduce((sum, data) => sum + (data.energyFromSolar ?? 0), 0),
      energyToBattery: data.reduce((sum, data) => sum + (data.energyToBattery ?? 0), 0),
      energyFromBattery: aggregatedEnergyFromBattery,
      energyFromBattery_corrected: useCorrectedValues ? aggregatedEnergyFromBattery : undefined,
      energyToGrid: aggregatedEnergyToGrid,
      energyToGrid_corrected: useCorrectedValues ? aggregatedEnergyToGrid : undefined,
      energyFromGrid: data.reduce((sum, data) => sum + (data.energyFromGrid ?? 0), 0),
      selfConsumptionFromProduction: aggregatedSelfConsumptionFromProduction,
      selfConsumptionFromProduction_corrected: useCorrectedValues ? aggregatedSelfConsumptionFromProduction : undefined,
      selfConsumptionFromConsumption: aggregatedSelfConsumptionFromConsumption,
      selfConsumptionFromConsumption_corrected: useCorrectedValues ? aggregatedSelfConsumptionFromConsumption : undefined,
      energyFromConsumptionToProduction: data.reduce((sum, data) => sum + (data.energyFromConsumptionToProduction ?? 0), 0),
      energyConsumed: data.reduce((sum, data) => sum + (data.energyConsumed ?? 0), 0),
    };

    return this.getBalanceDataRow(aggregatedData, useCorrectedValues);
  }

  // Independence calculations
  protected getIndependenceWithoutBattery(data: EnergyBalanceResponse, useCorrectedValues = false): number {
    const selfConsumption = this.getSelfConsumptionFromConsumption(data, useCorrectedValues);
    const totalConsumption = this.getTotalConsumption(data, useCorrectedValues);
    return totalConsumption > 0 ? (selfConsumption / totalConsumption) * 100 : 0;
  }

  protected getIndependenceWithBattery(data: EnergyBalanceResponse, useCorrectedValues = false): number {
    const selfConsumption = this.getSelfConsumptionFromConsumption(data, useCorrectedValues);
    const fromBattery = this.getEnergyFromBattery(data, useCorrectedValues);
    const totalConsumption = this.getTotalConsumption(data, useCorrectedValues);
    return totalConsumption > 0 ? ((selfConsumption + fromBattery) / totalConsumption) * 100 : 0;
  }

  protected getSelfConsumptionWithoutBattery(data: EnergyBalanceResponse, useCorrectedValues = false): number {
    const selfConsumption = this.getSelfConsumptionFromProduction(data, useCorrectedValues);
    const totalProduction = this.getTotalProduction(data, useCorrectedValues);
    return totalProduction > 0 ? (selfConsumption / totalProduction) * 100 : 0;
  }

  protected getSelfConsumptionWithBattery(data: EnergyBalanceResponse, useCorrectedValues = false): number {
    const selfConsumption = this.getSelfConsumptionFromProduction(data, useCorrectedValues);
    const toBattery = data?.energyToBattery ?? 0;
    const totalProduction = this.getTotalProduction(data, useCorrectedValues);
    return totalProduction > 0 ? ((selfConsumption + toBattery) / totalProduction) * 100 : 0;
  }

  protected getIndependenceDataRow(data: EnergyBalanceResponse, useCorrectedValues = false): BalanceDataRow[] {
    const independenceWithoutBattery = this.getIndependenceWithoutBattery(data, useCorrectedValues);
    const independenceWithBattery = this.getIndependenceWithBattery(data, useCorrectedValues);
    const selfConsumptionWithoutBattery = this.getSelfConsumptionWithoutBattery(data, useCorrectedValues);
    const selfConsumptionWithBattery = this.getSelfConsumptionWithBattery(data, useCorrectedValues);

    return [
      {
        label: 'Autarkie:',
        value: '',
        isTotal: true,
        isFirstElement: true,
      },
      {
        label: 'Autarkie <span>ohne</span> Batterie',
        value: `${independenceWithoutBattery.toFixed(1)}%`,
      },
      {
        label: 'Autarkie <span>mit</span> Batterie',
        value: `${independenceWithBattery.toFixed(1)}%`,
      },
      {
        label: 'Eigennutzung:',
        value: '',
        isTotal: true,
      },
      {
        label: 'Eigennutzung <span>ohne</span> Batterie',
        value: `${selfConsumptionWithoutBattery.toFixed(1)}%`,
      },
      {
        label: 'Eigennutzung <span>mit</span> Batterie',
        value: `${selfConsumptionWithBattery.toFixed(1)}%`,
      },
    ];
  }

  protected getAggregatedIndependenceDataRow(data: EnergyBalanceResponse[], useCorrectedValues = false): BalanceDataRow[] {
    const aggregatedData = this.getAggregatedAverageResponse(data, useCorrectedValues);
    return this.getIndependenceDataRow(aggregatedData, useCorrectedValues);
  }

  protected getAggregatedAverageResponse(data: EnergyBalanceResponse[], useCorrectedValues = false) : EnergyBalanceResponse{
    const aggregatedEnergyFromBattery = data.reduce((sum, item) => sum + this.getEnergyFromBattery(item, useCorrectedValues), 0);
    const aggregatedEnergyToGrid = data.reduce((sum, item) => sum + this.getEnergyToGrid(item, useCorrectedValues), 0);
    const aggregatedSelfConsumptionFromProduction = data.reduce(
      (sum, item) => sum + this.getSelfConsumptionFromProduction(item, useCorrectedValues),
      0,
    );
    const aggregatedSelfConsumptionFromConsumption = data.reduce(
      (sum, item) => sum + this.getSelfConsumptionFromConsumption(item, useCorrectedValues),
      0,
    );

    const aggregatedData: EnergyBalanceResponse = {
      date: undefined,
      energyFromSolar: data.reduce((sum, data) => sum + (data.energyFromSolar ?? 0), 0),
      energyToBattery: data.reduce((sum, data) => sum + (data.energyToBattery ?? 0), 0),
      energyFromBattery: aggregatedEnergyFromBattery,
      energyFromBattery_corrected: useCorrectedValues ? aggregatedEnergyFromBattery : undefined,
      energyToGrid: aggregatedEnergyToGrid,
      energyToGrid_corrected: useCorrectedValues ? aggregatedEnergyToGrid : undefined,
      energyFromGrid: data.reduce((sum, data) => sum + (data.energyFromGrid ?? 0), 0),
      selfConsumptionFromProduction: aggregatedSelfConsumptionFromProduction,
      selfConsumptionFromProduction_corrected: useCorrectedValues ? aggregatedSelfConsumptionFromProduction : undefined,
      selfConsumptionFromConsumption: aggregatedSelfConsumptionFromConsumption,
      selfConsumptionFromConsumption_corrected: useCorrectedValues ? aggregatedSelfConsumptionFromConsumption : undefined,
      energyFromConsumptionToProduction: data.reduce((sum, data) => sum + (data.energyFromConsumptionToProduction ?? 0), 0),
      energyConsumed: data.reduce((sum, data) => sum + (data.energyConsumed ?? 0), 0),
    };
    return aggregatedData;
  }

  constructor() {
    // Check screen width on init
    if (window.innerWidth <= 768) { // <= 768px is "mobile" by convention, show Accordion details NOT on mobile (toggle it shut)
      this.isAccordionOpen = false;
    } else {
      this.isAccordionOpen = true;
    }
  }

}
